import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  MagnifyingGlassIcon, PlusIcon, EyeIcon, UserIcon, TagIcon, CalendarIcon, TrashIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import {
  STATI, ORDINE_STATI, StatoPill, formatEuro, formatData, clienteNome, clienteIniziali
} from './statiPratica';

const inputCls = 'w-full px-4 py-2.5 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl shadow-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/50 transition-all duration-200 placeholder-gray-400';
const labelCls = 'block text-sm font-medium text-gray-700 mb-1.5';

// ---- Modale Nuova pratica --------------------------------------------------
function NuovaPraticaModal({ onClose, onCreata }) {
  const [step, setStep] = useState('cliente');
  const [modoCliente, setModoCliente] = useState('cerca');
  const [ricerca, setRicerca] = useState('');
  const [risultati, setRisultati] = useState([]);
  const [clienteSel, setClienteSel] = useState(null);
  const [nuovoCliente, setNuovoCliente] = useState({ tipo_cliente: 'privato', nome: '', cognome: '', ragione_sociale: '', email: '', telefono: '', codice_fiscale: '' });
  const [ricercaBici, setRicercaBici] = useState('');
  const [biciRis, setBiciRis] = useState([]);
  const [biciSel, setBiciSel] = useState(null);
  const [form, setForm] = useState({ taglia: '', quantita: 1, prezzoVendita: '', acconto: '', metodoPagamento: '', dataConsegnaPrevista: '', note: '' });
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (modoCliente !== 'cerca') return;
    const t = setTimeout(async () => {
      try { const { data } = await api.get('/bici-pratiche/clienti', { params: { q: ricerca } }); setRisultati(Array.isArray(data) ? data : []); }
      catch (e) { /* ignora */ }
    }, 250);
    return () => clearTimeout(t);
  }, [ricerca, modoCliente]);

  useEffect(() => {
    const t = setTimeout(async () => {
      try { const { data } = await api.get('/bici', { params: { search: ricercaBici, limit: 1000 } }); setBiciRis(data?.bici || []); }
      catch (e) { /* ignora */ }
    }, 250);
    return () => clearTimeout(t);
  }, [ricercaBici]);

  const selezionaBici = (b) => {
    setBiciSel(b);
    if (b?.prezzoPubblico && !form.prezzoVendita) setForm(f => ({ ...f, prezzoVendita: b.prezzoPubblico }));
  };

  const vaiDettagli = () => {
    if (modoCliente === 'nuovo') {
      if (!nuovoCliente.nome && !nuovoCliente.ragione_sociale) { toast.warning('Inserisci almeno nome o ragione sociale'); return; }
    } else if (!clienteSel) { toast.warning('Seleziona un cliente'); return; }
    setStep('dettagli');
  };

  const salva = async () => {
    setSalvando(true);
    try {
      const payload = {
        idBici: biciSel?.id || null, taglia: form.taglia || null, quantita: form.quantita || 1,
        prezzoVendita: form.prezzoVendita || null, acconto: form.acconto || null,
        metodoPagamento: form.metodoPagamento || null, dataConsegnaPrevista: form.dataConsegnaPrevista || null, note: form.note || null
      };
      if (modoCliente === 'nuovo') payload.cliente = nuovoCliente; else payload.idCliente = clienteSel.id;
      const { data } = await api.post('/bici-pratiche', payload);
      toast.success('Pratica creata');
      onCreata(data);
    } catch (err) { toast.error(err.response?.data?.error || 'Errore nella creazione'); }
    finally { setSalvando(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-5 text-white">
          <p className="text-emerald-100 text-sm">Nuova pratica di vendita</p>
          <h3 className="text-2xl font-bold mt-0.5">{step === 'cliente' ? 'Chi è il cliente?' : 'Dettagli della vendita'}</h3>
          <div className="flex items-center gap-2 text-xs mt-3">
            <span className={`px-2.5 py-1 rounded-full ${step === 'cliente' ? 'bg-white text-emerald-700 font-semibold' : 'bg-white/20'}`}>1 · Cliente</span>
            <span className={`px-2.5 py-1 rounded-full ${step === 'dettagli' ? 'bg-white text-emerald-700 font-semibold' : 'bg-white/20'}`}>2 · Dettagli</span>
          </div>
        </div>

        <div className="px-6 py-5 overflow-y-auto">
          {step === 'cliente' ? (
            <div>
              <div className="flex gap-2 mb-4">
                {['cerca', 'nuovo'].map(m => (
                  <button key={m} onClick={() => setModoCliente(m)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${modoCliente === m ? 'bg-emerald-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    {m === 'cerca' ? 'Cliente esistente' : 'Nuovo cliente'}
                  </button>
                ))}
              </div>

              {modoCliente === 'cerca' ? (
                <div>
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input autoFocus value={ricerca} onChange={e => setRicerca(e.target.value)} placeholder="Cerca per nome, email, telefono…" className={`${inputCls} pl-12`} />
                  </div>
                  <div className="mt-3 divide-y divide-gray-100 max-h-64 overflow-y-auto border border-gray-100 rounded-xl">
                    {risultati.length === 0 && <p className="text-sm text-gray-400 px-4 py-6 text-center">Nessun cliente trovato.</p>}
                    {risultati.map(c => (
                      <button key={c.id} onClick={() => setClienteSel(c)}
                        className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-emerald-50/50 transition-colors ${clienteSel?.id === c.id ? 'bg-emerald-50' : ''}`}>
                        <span className="h-9 w-9 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">{clienteIniziali(c)}</span>
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-gray-900 truncate">{clienteNome(c)}</span>
                          <span className="block text-xs text-gray-400 truncate">{[c.email, c.telefono, c.citta].filter(Boolean).join(' · ') || '—'}</span>
                        </span>
                        {clienteSel?.id === c.id && <span className="ml-auto text-emerald-600 font-bold">✓</span>}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className={labelCls}>Tipo cliente</label>
                    <div className="flex gap-2">
                      {['privato', 'aziendale'].map(t => (
                        <button key={t} onClick={() => setNuovoCliente({ ...nuovoCliente, tipo_cliente: t })}
                          className={`px-4 py-2 rounded-xl text-sm font-medium border capitalize ${nuovoCliente.tipo_cliente === t ? 'bg-emerald-600 text-white border-emerald-600' : 'text-gray-600 border-gray-200'}`}>{t}</button>
                      ))}
                    </div>
                  </div>
                  {nuovoCliente.tipo_cliente === 'aziendale' && (
                    <div className="col-span-2"><label className={labelCls}>Ragione sociale</label><input value={nuovoCliente.ragione_sociale} onChange={e => setNuovoCliente({ ...nuovoCliente, ragione_sociale: e.target.value })} className={inputCls} /></div>
                  )}
                  <div><label className={labelCls}>Nome</label><input value={nuovoCliente.nome} onChange={e => setNuovoCliente({ ...nuovoCliente, nome: e.target.value })} className={inputCls} /></div>
                  <div><label className={labelCls}>Cognome</label><input value={nuovoCliente.cognome} onChange={e => setNuovoCliente({ ...nuovoCliente, cognome: e.target.value })} className={inputCls} /></div>
                  <div><label className={labelCls}>Email</label><input value={nuovoCliente.email} onChange={e => setNuovoCliente({ ...nuovoCliente, email: e.target.value })} className={inputCls} /></div>
                  <div><label className={labelCls}>Telefono</label><input value={nuovoCliente.telefono} onChange={e => setNuovoCliente({ ...nuovoCliente, telefono: e.target.value })} className={inputCls} /></div>
                  <div className="col-span-2"><label className={labelCls}>Codice fiscale</label><input value={nuovoCliente.codice_fiscale} onChange={e => setNuovoCliente({ ...nuovoCliente, codice_fiscale: e.target.value })} className={inputCls} /></div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Prodotto</label>
                {biciSel ? (
                  <div className="flex items-center gap-3 p-3 border border-emerald-200 rounded-xl bg-emerald-50/50">
                    <div className="p-2 bg-emerald-100 rounded-lg"><TagIcon className="h-5 w-5 text-emerald-600" /></div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{biciSel.brand} {biciSel.modello}</p>
                      <p className="text-xs text-gray-400 truncate">{biciSel.nomeProdotto || biciSel.ean || ''}</p>
                    </div>
                    <button onClick={() => setBiciSel(null)} className="ml-auto text-xs text-gray-500 hover:text-gray-900 underline">cambia</button>
                  </div>
                ) : (
                  <div>
                    <input value={ricercaBici} onChange={e => setRicercaBici(e.target.value)} placeholder="Cerca una bici del catalogo (opzionale)…" className={inputCls} />
                    {biciRis.length > 0 && (
                      <div className="mt-2 divide-y divide-gray-100 max-h-44 overflow-y-auto border border-gray-100 rounded-xl">
                        {biciRis.map(b => (
                          <button key={b.id} onClick={() => selezionaBici(b)} className="w-full text-left px-4 py-2.5 hover:bg-emerald-50/50 flex items-center justify-between gap-3">
                            <span className="min-w-0"><span className="block text-sm font-medium text-gray-900 truncate">{b.brand} {b.modello}</span><span className="block text-xs text-gray-400 truncate">{b.nomeProdotto || b.ean}</span></span>
                            <span className="text-xs text-gray-500 whitespace-nowrap">{formatEuro(b.prezzoPubblico)}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Taglia</label><input value={form.taglia} onChange={e => setForm({ ...form, taglia: e.target.value })} placeholder="S / M / L…" className={inputCls} /></div>
                <div><label className={labelCls}>Quantità</label><input type="number" min="1" value={form.quantita} onChange={e => setForm({ ...form, quantita: e.target.value })} className={inputCls} /></div>
                <div><label className={labelCls}>Prezzo di vendita</label><input type="number" step="0.01" value={form.prezzoVendita} onChange={e => setForm({ ...form, prezzoVendita: e.target.value })} placeholder="€" className={inputCls} /></div>
                <div><label className={labelCls}>Acconto</label><input type="number" step="0.01" value={form.acconto} onChange={e => setForm({ ...form, acconto: e.target.value })} placeholder="€" className={inputCls} /></div>
                <div>
                  <label className={labelCls}>Pagamento</label>
                  <select value={form.metodoPagamento} onChange={e => setForm({ ...form, metodoPagamento: e.target.value })} className={inputCls}>
                    <option value="">—</option><option value="contanti">Contanti</option><option value="bonifico">Bonifico</option><option value="carta">Carta</option><option value="finanziamento">Finanziamento</option><option value="altro">Altro</option>
                  </select>
                </div>
                <div><label className={labelCls}>Consegna prevista</label><input type="date" value={form.dataConsegnaPrevista} onChange={e => setForm({ ...form, dataConsegnaPrevista: e.target.value })} className={inputCls} /></div>
              </div>
              <div><label className={labelCls}>Note</label><textarea rows="2" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} className={inputCls} /></div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/60">
          {step === 'dettagli'
            ? <button onClick={() => setStep('cliente')} className="px-4 py-2.5 text-sm text-gray-600 hover:text-gray-900">← Indietro</button>
            : <button onClick={onClose} className="px-4 py-2.5 text-sm text-gray-500 hover:text-gray-900">Annulla</button>}
          {step === 'cliente'
            ? <button onClick={vaiDettagli} className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-500/25">Continua →</button>
            : <button onClick={salva} disabled={salvando} className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-500/25 disabled:opacity-50">{salvando ? 'Creazione…' : 'Crea pratica'}</button>}
        </div>
      </div>
    </div>
  );
}

// ---- Pagina lista ----------------------------------------------------------
export default function BikePratichePage() {
  const navigate = useNavigate();
  const [pratiche, setPratiche] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [search, setSearch] = useState('');
  const [showNuova, setShowNuova] = useState(false);
  const [conteggi, setConteggi] = useState({});

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/bici-pratiche', { params: { stato: filtro || undefined, search: search || undefined } });
      setPratiche(Array.isArray(data) ? data : []);
    } catch (e) { toast.error('Errore nel caricamento delle pratiche'); }
    finally { setLoading(false); }
  }, [filtro, search]);

  useEffect(() => { carica(); }, [carica]);

  useEffect(() => {
    (async () => { try { const { data } = await api.get('/bici-pratiche/stats'); setConteggi(data?.perStato || {}); } catch (e) {} })();
  }, [pratiche.length]);

  const onCreata = (nuova) => { setShowNuova(false); navigate(`/bike/pratiche/${nuova.id}`); };

  const eliminaPratica = async (e, p) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare la pratica ${p.codicePratica}? L'operazione è irreversibile e rimuove anche i documenti allegati.`)) return;
    try {
      await api.delete(`/bici-pratiche/${p.id}`);
      setPratiche(prev => prev.filter(x => x.id !== p.id));
      toast.success('Pratica eliminata');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Errore nell\'eliminazione della pratica');
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-gray-50/50 via-white to-emerald-50/30">
      <div className="max-w-[1800px] mx-auto px-4 md:px-6 py-8 space-y-6">

        {/* Header */}
        <div className="bg-white/80 backdrop-blur-xl shadow-lg shadow-gray-200/20 rounded-2xl border border-gray-100/50 p-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Pratiche di vendita</h1>
              <p className="mt-2 text-gray-600">{pratiche.length} pratiche{filtro ? ` · ${STATI[filtro]?.label || filtro}` : ''}</p>
            </div>
            <button onClick={() => setShowNuova(true)}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl font-medium shadow-lg shadow-emerald-500/25 hover:shadow-xl hover:shadow-emerald-500/30 transform hover:scale-105 transition-all duration-200">
              <PlusIcon className="h-5 w-5 mr-2" /> Nuova pratica
            </button>
          </div>
        </div>

        {/* Filtri */}
        <div className="bg-white/80 backdrop-blur-xl shadow-lg shadow-gray-200/20 rounded-2xl border border-gray-100/50 p-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex-1 max-w-md relative">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input type="text" placeholder="Cerca pratica o cliente…" value={search} onChange={e => setSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl shadow-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/50 transition-all duration-200 placeholder-gray-400" />
            </div>
            <select value={filtro} onChange={e => setFiltro(e.target.value)}
              className="px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl shadow-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/50 transition-all duration-200 font-medium text-gray-700">
              <option value="">Tutti gli stati</option>
              {ORDINE_STATI.map(s => (
                <option key={s} value={s}>{STATI[s].label}{conteggi[s] ? ` (${conteggi[s]})` : ''}</option>
              ))}
            </select>
            {(search || filtro) && (
              <button onClick={() => { setSearch(''); setFiltro(''); }}
                className="px-6 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl shadow-sm text-gray-600 hover:text-gray-900 hover:bg-white/90 transition-all duration-200 font-medium">Reset</button>
            )}
          </div>
        </div>

        {/* Lista */}
        <div className="bg-white/80 backdrop-blur-xl shadow-lg shadow-gray-200/20 rounded-2xl border border-gray-100/50 overflow-hidden">
          {loading ? (
            <div className="p-16 text-center">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-2 border-emerald-200 border-t-emerald-600" />
              <p className="mt-6 text-gray-600 font-medium">Caricamento pratiche…</p>
            </div>
          ) : pratiche.length > 0 ? (
            <div className="divide-y divide-gray-100/50">
              {pratiche.map(p => {
                const saldo = (Number(p.prezzoVendita) || 0) - (Number(p.acconto) || 0);
                return (
                  <div key={p.id} onClick={() => navigate(`/bike/pratiche/${p.id}`)}
                    className="p-5 md:p-6 hover:bg-gradient-to-r hover:from-emerald-50/30 hover:to-white/30 transition-all duration-200 group cursor-pointer">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-4 md:gap-6 min-w-0 flex-1">
                        <div className="min-w-0">
                          <h3 className="text-lg font-bold text-gray-900 group-hover:text-emerald-700 transition-colors">{p.codicePratica}</h3>
                          <p className="text-xs text-gray-400">ID #{p.id}</p>
                        </div>
                        <StatoPill stato={p.statoPratica} size="sm" />
                        {/* Cliente */}
                        <div className="hidden md:flex items-center gap-2.5 min-w-0">
                          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-[11px] font-semibold flex items-center justify-center flex-shrink-0">{clienteIniziali(p.cliente)}</div>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 truncate max-w-[160px]">{clienteNome(p.cliente)}</p>
                            <p className="text-xs text-gray-500 truncate max-w-[160px]">{p.cliente?.email || '—'}</p>
                          </div>
                        </div>
                        {/* Prodotto */}
                        <div className="hidden lg:flex items-center gap-2 min-w-0">
                          <div className="p-2 bg-gray-50 rounded-lg"><TagIcon className="h-4 w-4 text-gray-500" /></div>
                          <p className="text-sm text-gray-700 truncate max-w-[180px]">{p.bici ? `${p.bici.brand} ${p.bici.modello}` : <span className="text-gray-400 italic">Nessun prodotto</span>}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 md:gap-6">
                        <div className="text-right">
                          <p className="text-lg font-bold text-emerald-700">{formatEuro(p.prezzoVendita, '—')}</p>
                          {p.acconto ? <p className="text-xs text-gray-400">saldo {formatEuro(saldo)}</p> : <p className="text-xs text-gray-400">{formatData(p.updatedAt)}</p>}
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/bike/pratiche/${p.id}`); }}
                          className="hidden sm:inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-gray-700 to-gray-800 text-white rounded-xl text-sm font-medium shadow-lg shadow-gray-500/20 hover:shadow-xl transform hover:scale-105 transition-all">
                          <EyeIcon className="h-4 w-4 mr-1.5" /> Dettagli
                        </button>
                        <button onClick={(e) => eliminaPratica(e, p)} title="Elimina pratica"
                          className="inline-flex items-center justify-center h-10 w-10 rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-100 transition-all">
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    {/* riga mobile cliente */}
                    <div className="md:hidden mt-2 text-sm text-gray-600">{clienteNome(p.cliente)} · {p.bici ? `${p.bici.brand} ${p.bici.modello}` : 'nessun prodotto'}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-16 text-center">
              <ClipboardEmpty />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Nessuna pratica</h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">Crea la prima pratica di vendita per iniziare.</p>
              <button onClick={() => setShowNuova(true)}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl font-semibold shadow-lg shadow-emerald-500/25 hover:shadow-xl transform hover:scale-105 transition-all duration-200">
                <PlusIcon className="h-5 w-5 mr-2" /> Nuova pratica
              </button>
            </div>
          )}
        </div>
      </div>

      {showNuova && <NuovaPraticaModal onClose={() => setShowNuova(false)} onCreata={onCreata} />}
    </div>
  );
}

function ClipboardEmpty() {
  return (
    <div className="mx-auto h-16 w-16 text-gray-300 mb-6">
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    </div>
  );
}
